import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@shared/components/ui/button';
import { BookingPageLayout } from '@features/booking/layouts';
import { useAuth } from '@core/auth/AuthProvider';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { api, getAuthToken } from '@core/api';
import { COMPLETE_PROFILE_PATH } from '@core/auth';
import { captureAuthError } from '@core/sentry/utils';
import { PhoneInput } from '@shared/components/common/PhoneInput';
import { isValidPhone } from '@shared/utils/phoneValidation';

const CompleteProfilePage = () => {
  const { t } = useTranslation('booking');
  const { authState, updateProfile } = useAuth();
  const restaurant = useRestaurant();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState(authState.name || '');
  const [phone, setPhone] = useState(authState.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');

    if (!name.trim()) {
      setError(t('COMPLETE_PROFILE_NAME_REQUIRED'));
      return;
    }

    if (!isValidPhone(phone)) {
      setError(t('COMPLETE_PROFILE_PHONE_REQUIRED'));
      return;
    }

    // Defensive guard: /api/update-user-info requires a valid JWT. If
    // storage was cleared mid-flow, we must not fire a request that's
    // guaranteed to return 401. Dispatching `auth:expired` triggers
    // AuthProvider.logout(), which flips isAuthenticated and puts SIGN_IN
    // back in activeSteps — goToStep would silently no-op here because
    // SIGN_IN is gated on !isAuthenticated and React state still says we
    // are authenticated in this failure mode.
    if (!getAuthToken()) {
      Sentry.addBreadcrumb({
        category: 'complete-profile-guard',
        message: 'Token missing at complete-profile, triggering auth:expired',
        level: 'warning',
      });
      captureAuthError('auth.complete_profile_no_token', {});
      window.dispatchEvent(
        new CustomEvent('auth:expired', {
          detail: { from: window.location.pathname },
        }),
      );
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/update-user-info', {
        email: authState.email,
        name: name.trim(),
        phone: phone.trim(),
      });

      updateProfile({ name: name.trim(), phone: phone.trim() });

      // Return to the route the enforcer redirected us from. Falls back to
      // home when the user landed on /complete-profile directly (manual URL
      // or post-auth gate without a prior route to resume).
      const fromState = (location.state as { from?: string } | null)?.from;
      const target = fromState && fromState !== COMPLETE_PROFILE_PATH ? fromState : '/';
      navigate(target, { replace: true });
    } catch {
      setError(t('COMPLETE_PROFILE_ERROR'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BookingPageLayout
      title={t('COMPLETE_PROFILE_TITLE')}
      subtitle={t('COMPLETE_PROFILE_SUBTITLE')}
      showBack={false}
    >
      <div className="w-full px-6 space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('COMPLETE_PROFILE_NAME')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
            required
          />
        </div>

        {/* Email (disabled) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('COMPLETE_PROFILE_EMAIL')}
          </label>
          <input
            type="email"
            value={authState.email || ''}
            disabled
            className="w-full rounded-xl border border-border bg-foreground/5 p-3 text-foreground/50 cursor-not-allowed"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('COMPLETE_PROFILE_PHONE')}
          </label>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            defaultCountry={restaurant.isoCountryCode}
            dialCode={restaurant.countryCode}
            className="rounded-xl border border-border p-3 focus-within:border-brand"
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}
      </div>

      <div className="w-full px-6 mt-12">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading && <Loader2 className="h-5 w-5 animate-spin" />}
          {t('COMPLETE_PROFILE_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default CompleteProfilePage;
